import { MetricId } from '../../../../../../api/types-graphql';
import { formatMetricValue } from '../../../../../../utils-display';
import { SortTypes } from '../../../../../types';
import {
  ChartData,
  SingleDatasetChartData,
  SortedLabels,
} from '../../../../ui-components/data-viz-ui/charts/common/common-chart-types';
import {
  ComponentStateManager,
  DataVizContainerCommonUtils,
} from '../../../common/types';
import {
  MultiMetricComparisonVizData,
  MultiMetricComparisonVizDataResponse,
  MultiMetricComparisonVizExternalProps,
} from '../../types';
import {
  MultiMetricComparisonBarChartActions,
  MultiMetricComparisonBarChartState,
  MultiMetricComparisonBarChartUiComponentProps,
} from './types';

/**
 * Transforms the multi-metric comparison data response into ChartData format
 * suitable for rendering in a bar chart.
 *
 * Each metric becomes a label (category) with its value as the bar height/width.
 * Colors per bar are handled via colorGetter in the chart component.
 */
const getChartDataFromMultiMetricResponse = (
  response: MultiMetricComparisonVizDataResponse,
  metricDetails: MultiMetricComparisonVizData['metricDetails']
): ChartData => {
  const { metricValues } = response;

  if (!metricValues.length) {
    throw new Error('Invalid State. metricValues is empty');
  }

  // Use the first metric's format type for value formatting
  // Assumption: all metrics have the same format type
  const firstMetricDetails =
    metricDetails[metricValues[0].metric.value as unknown as MetricId];
  if (!firstMetricDetails) {
    throw new Error(
      `Invalid state. Metric details undefined for metric: ${metricValues[0].metric.value}`
    );
  }

  const datasetKey = 'metrics';
  const labels = metricValues.map((mv) => mv.label);

  // Sort labels by value for ASC/DESC ordering
  const sortedByValueAsc = [...metricValues].sort((a, b) => a.value - b.value);
  const sortedByValueDesc = [...metricValues].sort((a, b) => b.value - a.value);

  const sortedLabels: SortedLabels = {
    [SortTypes.ASC]: sortedByValueAsc.map((mv) => mv.label),
    [SortTypes.DESC]: sortedByValueDesc.map((mv) => mv.label),
  };

  const dataMap: Record<string, { datasetKey: string; value: number }[]> =
    Object.fromEntries(
      metricValues.map((mv) => [
        mv.label,
        [
          {
            datasetKey,
            value: mv.value,
          },
        ],
      ])
    );

  const chartData: SingleDatasetChartData = {
    type: 'single-dataset',
    labels,
    sortedLabels,
    dataMap,
    datasets: [
      {
        value: datasetKey,
        label: '',
        valueFormatter: (val: number) => {
          return formatMetricValue(val, firstMetricDetails.formatType);
        },
      },
    ],
  };

  return chartData;
};

export const multiMetricComparisonBarChartDataToUiComponentProps = (
  data: MultiMetricComparisonVizData,
  externalProps: MultiMetricComparisonVizExternalProps,
  componentStateManager: ComponentStateManager<
    MultiMetricComparisonBarChartState,
    MultiMetricComparisonBarChartActions
  >,
  _commonUtils: DataVizContainerCommonUtils
): MultiMetricComparisonBarChartUiComponentProps => {
  const { fullScreenHandle } = externalProps;
  const { dataResponse, titleConfig, metricDetails } = data;

  const chartData = getChartDataFromMultiMetricResponse(
    dataResponse,
    metricDetails
  );

  const displayConfig = {
    ...componentStateManager.state.displayState,
    ...componentStateManager.actions.displayActions,
  };

  return {
    fullScreenHandle,
    displayConfig,
    titleConfig,
    chartData,
    chartColors: data.chartColors,
    isHorizontal: data.isHorizontal,
  };
};
